import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import { StrikingBadge } from "@/components/keywords/StrikingBadge";
import { Button } from "@/components/ui/Button";
import { api, type GscKeywordRow } from "@/lib/api";
import { cn } from "@/lib/formatters";

interface KeywordsTableProps {
  site: string;
  rows: GscKeywordRow[];
  onGenerated?: () => void;
}

type Status = "idle" | "dispatching" | "done" | "error";

interface RowState {
  status: Status;
  message?: string;
}

function pctFmt(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

function posColor(pos: number): string {
  if (pos <= 10) return "text-success";
  if (pos <= 20) return "text-primary";
  if (pos <= 30) return "text-warn";
  return "text-muted";
}

export function KeywordsTable({ site, rows, onGenerated }: KeywordsTableProps) {
  const [rowState, setRowState] = useState<Record<string, RowState>>({});

  async function handleGenerate(row: GscKeywordRow) {
    setRowState((s) => ({ ...s, [row.query]: { status: "dispatching" } }));
    try {
      await api.gsc.generate({
        query: row.query,
        site,
        position: row.position,
        impressions: row.impressions,
      });
      setRowState((s) => ({ ...s, [row.query]: { status: "done" } }));
      onGenerated?.();
    } catch (e) {
      setRowState((s) => ({
        ...s,
        [row.query]: { status: "error", message: e instanceof Error ? e.message : "failed" },
      }));
    }
  }

  if (rows.length === 0) {
    return (
      <div className="surface p-12 text-center">
        <p className="font-mono text-sm text-muted">$ no queries match the filters</p>
        <p className="mt-2 text-xs text-muted/70">
          Probá ampliar el rango de posición o bajar el mínimo de impresiones.
        </p>
      </div>
    );
  }

  return (
    <div className="surface overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bg-elev/40 border-b border-border">
            <tr className="text-left text-xs font-mono uppercase tracking-wider text-muted">
              <th className="px-4 py-3">query</th>
              <th className="px-3 py-3 text-right">pos</th>
              <th className="px-3 py-3 text-right">impr</th>
              <th className="px-3 py-3 text-right">clicks</th>
              <th className="px-3 py-3 text-right">ctr</th>
              <th className="px-3 py-3">distance</th>
              <th className="px-3 py-3 text-right">action</th>
            </tr>
          </thead>
          <tbody>
            <AnimatePresence initial={false}>
              {rows.map((row, idx) => {
                const state = rowState[row.query];
                const busy = state?.status === "dispatching";
                const done = state?.status === "done";
                const errored = state?.status === "error";
                return (
                  <motion.tr
                    key={row.query}
                    layout
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: Math.min(idx, 12) * 0.015 }}
                    className={cn(
                      "border-b border-border/50 hover:bg-primary/[0.02] transition-colors",
                      done && "bg-success/5",
                      errored && "bg-danger/5",
                    )}
                  >
                    <td className="px-4 py-3">
                      <div className="text-text truncate max-w-md">{row.query}</div>
                      {state?.message && (
                        <div className="mt-1 text-xs font-mono text-danger truncate max-w-md">
                          {state.message}
                        </div>
                      )}
                    </td>
                    <td className={cn("px-3 py-3 text-right font-mono tabular-nums", posColor(row.position))}>
                      {row.position.toFixed(1)}
                    </td>
                    <td className="px-3 py-3 text-right font-mono tabular-nums text-text">
                      {row.impressions.toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-right font-mono tabular-nums text-muted">
                      {row.clicks.toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-right font-mono tabular-nums text-muted">
                      {pctFmt(row.ctr)}
                    </td>
                    <td className="px-3 py-3">
                      <StrikingBadge striking={row.striking_distance} />
                    </td>
                    <td className="px-3 py-3 text-right">
                      {done ? (
                        <span className="font-mono text-xs text-success uppercase tracking-wider">
                          ✓ dispatched
                        </span>
                      ) : (
                        <Button
                          variant={errored ? "danger" : "primary"}
                          onClick={() => handleGenerate(row)}
                          disabled={busy}
                          className="px-3 py-1.5 text-xs"
                        >
                          {busy ? "…" : errored ? "retry" : "→ generar"}
                        </Button>
                      )}
                    </td>
                  </motion.tr>
                );
              })}
            </AnimatePresence>
          </tbody>
        </table>
      </div>
    </div>
  );
}
